import React, {
    useState,
    useEffect,
    useReducer,
    useCallback,
    useContext,
} from "react";
import { toast } from "react-toastify";
import { makeStyles, useTheme } from "@material-ui/core/styles";

import {
  TextField,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Paper,
  Avatar,
  IconButton,
  Card,
  CardContent,
  Box,
  Typography,
  Tabs,
  Tab,
  Divider,
  Chip,
  useMediaQuery,
  CircularProgress as MUICircularProgress,
} from "@material-ui/core";

import {
  DeleteOutline as DeleteOutlineIcon,
  Edit as EditIcon,
  Search as SearchIcon,
  Description as DescriptionIcon,
  AttachFile as AttachFileIcon,
} from "@material-ui/icons";

import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import FileModal from "../../components/FileModal";
import ConfirmationModal from "../../components/ConfirmationModal";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import ForbiddenPage from "../../components/ForbiddenPage";
import MainContainer from "../../components/MainContainer";
import {
  AddCircle,
  Build,
  CloudUpload as CloudUploadIcon,
  InsertDriveFile as InsertDriveFileIcon,
} from "@mui/icons-material";
import AddIcon from '@mui/icons-material/Add';

import {
  Button,
  CircularProgress,
  Grid,
  Stack,
} from "@mui/material";

const reducer = (state, action) => {
    if (action.type === "LOAD_FILES") {
        const files = action.payload;
        const newFiles = [];

        files.forEach((fileList) => {
            const fileListIndex = state.findIndex((s) => s.id === fileList.id);
            if (fileListIndex !== -1) {
                state[fileListIndex] = fileList;
            } else {
                newFiles.push(fileList);
            }
        });

        return [...state, ...newFiles];
    }

    if (action.type === "UPDATE_FILES") {
        const fileList = action.payload;
        const fileListIndex = state.findIndex((s) => s.id === fileList.id);

        if (fileListIndex !== -1) {
            state[fileListIndex] = fileList;
            return [...state];
        } else {
            return [fileList, ...state];
        }
    }

    if (action.type === "DELETE_FILE") {
        const fileListId = action.payload;

        const fileListIndex = state.findIndex((s) => s.id === fileListId);
        if (fileListIndex !== -1) {
            state.splice(fileListIndex, 1);
        }
        return [...state];
    }

    if (action.type === "RESET") {
        return [];
    }
};

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    width: "100%",
    flex: 1,
    padding: theme.spacing(1),
    overflowY: "scroll",
    backgroundColor: theme.palette.type === 'dark' ? "#1e1e1e" : "#ffffff",
    ...theme.scrollbarStyles,
    border: "none",
  },
  headerContainer: {
    background: "linear-gradient(90deg, #6517ab 0%, #8e44ad 100%)",
    padding: theme.spacing(3),
    borderRadius: "0 0 20px 20px",
    color: "white",
    marginBottom: theme.spacing(3),
    boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
  },
  headerTitle: {
    fontSize: "2rem",
    fontWeight: 800,
    marginBottom: theme.spacing(1),
  },
  headerSubtitle: {
    fontSize: "1rem",
    opacity: 0.9,
    marginBottom: theme.spacing(2),
  },
  searchPaper: {
    padding: "4px 16px",
    display: "flex",
    alignItems: "center",
    borderRadius: "15px",
    backgroundColor: "rgba(255, 255, 255, 0.15)",
    backdropFilter: "blur(10px)",
    border: "1px solid rgba(255, 255, 255, 0.2)",
    "& .MuiInputBase-root": {
      color: "white",
    },
    "& .MuiInputBase-input::placeholder": {
      color: "rgba(255, 255, 255, 0.7)",
      opacity: 1,
    },
  },
  addButton: {
    background: theme.palette.background.paper,
    color: "#6517ab",
    borderRadius: "12px",
    padding: "10px 24px",
    fontWeight: "700",
    textTransform: "none",
    transition: "all 0.3s ease",
    "&:hover": {
      background: theme.palette.type === "light" ? "#f0f0f0" : "rgba(255, 255, 255, 0.1)",
      transform: "translateY(-2px)",
      boxShadow: "0 4px 15px rgba(0,0,0,0.1)",
    },
  },
  tabContainer: {
    marginTop: theme.spacing(2),
  },
  tabs: {
    minHeight: "40px",
    "& .MuiTabs-indicator": {
      backgroundColor: "white",
      height: "3px",
      borderRadius: "3px",
    },
  },
  tab: {
    textTransform: "none",
    fontWeight: 700,
    fontSize: "1rem",
    color: "rgba(255, 255, 255, 0.7)",
    minWidth: "120px",
    minHeight: "40px",
    "&.Mui-selected": {
      color: "white",
    },
  },
  premiumCard: {
    borderRadius: "15px",
    transition: "all 0.3s ease",
    backgroundColor: theme.palette.type === 'dark' ? "#1e1e1e" : "#ffffff",
    border: `1px solid ${theme.palette.type === 'dark' ? "rgba(255, 255, 255, 0.08)" : "rgba(0, 0, 0, 0.08)"}`,
    "&:hover": {
      boxShadow: "0 8px 30px rgba(101, 23, 171, 0.15)",
      transform: "translateY(-5px)",
    },
  },
  iconButton: {
    borderRadius: "10px",
    padding: "8px",
    transition: "all 0.2s ease",
    "&:hover": {
      transform: "scale(1.1)",
    },
  },
  tableRow: {
    transition: "all 0.2s",
    "&:hover": {
      backgroundColor: theme.palette.type === 'dark' ? "rgba(255, 255, 255, 0.02)" : "rgba(101, 23, 171, 0.05)",
    },
    cursor: "pointer",
  },
}));

const FileLists = () => {
    const classes = useStyles();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const { user, socket } = useContext(AuthContext);

    const [loading, setLoading] = useState(false);
    const [pageNumber, setPageNumber] = useState(1);
    const [hasMore, setHasMore] = useState(false);
    const [selectedFileList, setSelectedFileList] = useState(null);
    const [deletingFileList, setDeletingFileList] = useState(null);
    const [confirmModalOpen, setConfirmModalOpen] = useState(false);
    const [searchParam, setSearchParam] = useState("");
    const [files, dispatch] = useReducer(reducer, []);
    const [fileListModalOpen, setFileListModalOpen] = useState(false);
    const [tabValue, setTabValue] = useState("table");

    const fetchFileLists = useCallback(async () => {
        try {
            const { data } = await api.get("/files/", {
                params: { searchParam, pageNumber },
            });
            dispatch({ type: "LOAD_FILES", payload: data.files });
            setHasMore(data.hasMore);
            setLoading(false);
        } catch (err) {
            toastError(err);
        }
    }, [searchParam, pageNumber]);

    useEffect(() => {
        dispatch({ type: "RESET" });
        setPageNumber(1);
    }, [searchParam]);

    useEffect(() => {
        setLoading(true);
        const delayDebounceFn = setTimeout(() => {
            fetchFileLists();
        }, 500);
        return () => clearTimeout(delayDebounceFn);
    }, [searchParam, pageNumber, fetchFileLists]);

    useEffect(() => {
        // const socket = socketManager.GetSocket(user.companyId, user.id);

        const onFileEvent = (data) => {
            if (data.action === "update" || data.action === "create") {
                dispatch({ type: "UPDATE_FILES", payload: data.files });
            }

            if (data.action === "delete") {
                dispatch({ type: "DELETE_FILE", payload: +data.fileId });
            }
        };

        socket.on(`company-${user.companyId}-file`, onFileEvent);
        return () => {
            socket.off(`company-${user.companyId}-file`, onFileEvent);
        };
    }, [socket]);

    const handleOpenFileListModal = () => {
        setSelectedFileList(null);
        setFileListModalOpen(true);
    };

    const handleCloseFileListModal = () => {
        setSelectedFileList(null);
        setFileListModalOpen(false);
    };

    const handleSearch = (event) => {
        setSearchParam(event.target.value.toLowerCase());
    };

    const handleEditFileList = (fileList) => {
        setSelectedFileList(fileList);
        setFileListModalOpen(true);
    };

    const handleDeleteFileList = async (fileListId) => {
        try {
            await api.delete(`/files/${fileListId}`);
            toast.success(i18n.t("files.toasts.deleted"));
        } catch (err) {
            toastError(err);
        }
        setDeletingFileList(null);
        setSearchParam("");
        setPageNumber(1);

        dispatch({ type: "RESET" });
        setPageNumber(1);
        await fetchFileLists();
    };

    const loadMore = () => {
        setPageNumber((prevState) => prevState + 1);
    };

    const handleScroll = (e) => {
        if (!hasMore || loading) return;
        const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
        if (scrollHeight - (scrollTop + 100) < clientHeight) {
            loadMore();
        }
    };

    const handleTabChange = (event, newValue) => {
        setTabValue(newValue);
    };

    const renderCardActions = (fileList) => (
        <Box display="flex" gap={1} justifyContent="center" width="100%" mt={tabValue === "grid" ? 2 : 0}>
            <IconButton
                size="small"
                onClick={() => handleEditFileList(fileList)}
                style={{ backgroundColor: theme.palette.type === 'dark' ? "rgba(25, 118, 210, 0.1)" : "#e3f2fd", color: "#1976d2" }}
                className={classes.iconButton}
                title="Editar"
            >
                <EditIcon fontSize="small" />
            </IconButton>
            <IconButton
                size="small"
                onClick={() => {
                    setConfirmModalOpen(true);
                    setDeletingFileList(fileList);
                }}
                style={{ backgroundColor: theme.palette.type === 'dark' ? "rgba(211, 47, 47, 0.1)" : "#ffebee", color: "#d32f2f" }}
                className={classes.iconButton}
                title="Excluir"
            >
                <DeleteOutlineIcon fontSize="small" />
            </IconButton>
        </Box>
    );

    return (
        <MainContainer>
            <ConfirmationModal
                title={deletingFileList && `${i18n.t("files.confirmationModal.deleteTitle")}`}
                open={confirmModalOpen}
                onClose={setConfirmModalOpen}
                onConfirm={() => handleDeleteFileList(deletingFileList.id)}
            >
                {i18n.t("files.confirmationModal.deleteMessage")}
            </ConfirmationModal>
            <FileModal
                open={fileListModalOpen}
                onClose={handleCloseFileListModal}
                reload={fetchFileLists}
                aria-labelledby="form-dialog-title"
                fileListId={selectedFileList && selectedFileList.id}
            />
            {user.profile === "user" ?
                <ForbiddenPage />
                :
                <>
            <Box className={classes.headerContainer}>
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} md={6}>
                        <Typography variant="h5" className={classes.headerTitle}>{i18n.t("files.title")}</Typography>
                        <Typography className={classes.headerSubtitle}>Gerencie e organize seus arquivos e listas de envio</Typography>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Box display="flex" gap={2} flexWrap="wrap" justifyContent={isMobile ? "center" : "flex-end"}>
                            <Paper className={classes.searchPaper} elevation={0}>
                                <InputAdornment position="start">
                                    <SearchIcon style={{ color: "rgba(255,255,255,0.7)" }} />
                                </InputAdornment>
                                <TextField
                                    placeholder="Pesquisar..."
                                    value={searchParam}
                                    onChange={handleSearch}
                                    InputProps={{ disableUnderline: true }}
                                />
                            </Paper>
                            <Button
                                variant="contained"
                                className={classes.addButton}
                                startIcon={<AddCircle />}
                                onClick={handleOpenFileListModal}
                            >
                                {i18n.t("files.buttons.add")}
                            </Button>
                        </Box>
                    </Grid>
                </Grid>

                <div className={classes.tabContainer}>
                    <Tabs
                        value={tabValue}
                        onChange={handleTabChange}
                        className={classes.tabs}
                    >
                        <Tab label="Lista" value="table" className={classes.tab} />
                        <Tab label="Grade" value="grid" className={classes.tab} />
                    </Tabs>
                </div>
            </Box>

            <Paper
                className={classes.mainPaper}
                elevation={0}
                onScroll={handleScroll}
            >
                {tabValue === "table" && (
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Nome do Arquivo</TableCell>
                                <TableCell align="center">Ações</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {files.map((fileList) => (
                                <TableRow key={fileList.id} className={classes.tableRow}>
                                    <TableCell>
                                        <Stack direction="row" alignItems="center" gap={1.5}>
                                            <Avatar style={{ backgroundColor: "#6517ab", width: 40, height: 40 }}>
                                                <InsertDriveFileIcon />
                                            </Avatar>
                                            <Box>
                                                <Typography variant="body1" style={{ fontWeight: 600, color: theme.palette.text.primary }}>
                                                    {fileList.name}
                                                </Typography>
                                                <Typography variant="caption" color="textSecondary">
                                                    ID: {fileList.id}
                                                </Typography>
                                            </Box>
                                        </Stack>
                                    </TableCell>
                                    <TableCell align="center">
                                        {renderCardActions(fileList)}
                                    </TableCell>
                                </TableRow>
                            ))}
                            {loading && <TableRowSkeleton columns={2} />}
                        </TableBody>
                    </Table>
                )}

                {tabValue === "grid" && (
                    <Grid container spacing={3} style={{ padding: "16px" }}>
                        {files.map((fileList) => (
                            <Grid item xs={12} sm={6} md={4} lg={3} key={fileList.id}>
                                <Card className={classes.premiumCard}>
                                    <CardContent style={{ textAlign: 'center' }}>
                                        <Box display="flex" justifyContent="center" mb={2}>
                                            <Avatar style={{ backgroundColor: "#6517ab", width: 60, height: 60 }}>
                                                <InsertDriveFileIcon style={{ fontSize: 30 }} />
                                            </Avatar>
                                        </Box>
                                        <Typography variant="h6" style={{ fontWeight: 700, color: theme.palette.text.primary }} gutterBottom>
                                            {fileList.name}
                                        </Typography>
                                        <Divider style={{ margin: '16px 0' }} />
                                        <Typography variant="caption" color="textSecondary" display="block">
                                            ID: <b>{fileList.id}</b>
                                        </Typography>
                                        {renderCardActions(fileList)}
                                    </CardContent>
                                </Card>
                            </Grid>
                        ))}
                        {loading && (
                            <Grid item xs={12} align="center">
                                <CircularProgress color="primary" />
                            </Grid>
                        )}
                    </Grid>
                )}
            </Paper>

                </>}
        </MainContainer>
    );
};

export default FileLists;
